import React from 'react';
import { FormattedMessage } from 'react-intl';

import { FormattedValue } from '../store/formatted-value';

import { Boiler } from '../atoms/icons/boiler';

import { Group } from '../organisms/group';
import { Animation } from '../organisms/animation';

import { SwitchBox } from '../molecules/switch-box';
import { ValueBox } from '../molecules/value-box';
import { Battery } from '../atoms/icons/battery';
import { HeatPump } from '../atoms/icons/heat-pump';
import { ElectricalGrid } from '../atoms/icons/electrical-grid';
import { SolarPanel } from '../atoms/icons/solar-panel';
import { House } from '../atoms/icons/house';
import { Thermometer } from '../atoms/icons/thermometer';
import { Wallbox } from '../atoms/icons/wallbox';
import { Wind } from '../atoms/icons/wind';
import { FormattedHeatPumpStatus } from '../atoms/formatted-heat-pump-status';
import { FormattedWallboxStatus } from '../atoms/formatted-wallbox-status';
import { FormattedStatus } from '../store/formatted-status';
import { useSelector } from '../store/utils';

import { PremiumBanner } from '../templates/premium/premium-banner';
import { InitialWizardBanner } from '../templates/initial-wizard/initial-wizard-banner';

const Home = () => {
  const [ecooneHP, acondHP, nibeHP] = useSelector(
    ({ variables: { HP_TYPE } }) =>
      [HP_TYPE === 1, HP_TYPE === 4, HP_TYPE === 2] as const
  );

  return (
    <>
      <Group
        title={
          <FormattedMessage
            id="homepage.statuses.title"
            defaultMessage="Režimy aktivních komponentů"
          />
        }
        help={{
          variant: 'tooltip',
          title: undefined,
          content: (
            <FormattedMessage
              id="homepage.statuses.tooltip"
              defaultMessage="Režimy aktivních komponentů"
            />
          ),
        }}
        layout="scrollable"
      >
        <SwitchBox
          visibility={{ variableId: 'HW_INSTALL' }}
          title={
            <FormattedMessage
              id="boiler.status.home"
              defaultMessage="Režim bojleru"
            />
          }
          subtitle={<FormattedStatus type="heatWater" />}
          switchVariableId="HW_ON"
          sx={{ mr: 2 }}
        />
        <SwitchBox
          visibility={{ variableId: 'PV_INSTALL' }}
          title={
            <FormattedMessage
              id="photovoltaics.status"
              defaultMessage="Režim FVE"
            />
          }
          subtitle={<FormattedStatus type="photovoltaic" />}
          sx={{ mr: 2 }}
        />
        <SwitchBox
          title={
            <FormattedMessage
              id="electrical_grid.status"
              defaultMessage="Stav distribuční sítě"
            />
          }
          subtitle={<FormattedStatus type="smartMeter" />}
          sx={{ mr: 2 }}
        />
        {/* ToDo Visibility */}
        <SwitchBox
          visibility={{ variableId: 'HP_ENABLED' }}
          title={
            <FormattedMessage
              id="heat_pump.status"
              defaultMessage="Režim tepelka"
            />
          }
          subtitle={<FormattedHeatPumpStatus />}
          sx={{ mr: 2 }}
        />
        <SwitchBox
          visibility={{ variableId: 'WB_INSTALL' }}
          title={
            <FormattedMessage
              id="wallbox.status.home"
              defaultMessage="Režim wallboxu"
            />
          }
          subtitle={<FormattedWallboxStatus />}
          switchVariableId="WB_ON"
          sx={{ mr: 2 }}
        />
        <SwitchBox
          visibility={{ variableId: 'PL_INSTALL' }}
          title={
            <FormattedMessage id="plug.status" defaultMessage="Režim zásuvky" />
          }
          subtitle={<FormattedStatus type="plug" index={0} />}
          switchVariableId="PL_ON[0]"
          sx={{ mr: 2 }}
        />
        <SwitchBox
          visibility={{ variableId: 'WIND_INSTALL' }}
          title={
            <FormattedMessage
              id="wind.status"
              defaultMessage="Režim větrné elektrárny"
            />
          }
          subtitle={<FormattedStatus type="wind" />}
          sx={{ mr: 2 }}
        />
      </Group>

      <InitialWizardBanner />

      <PremiumBanner />

      <Group
        title={
          <FormattedMessage id="homepage.animation" defaultMessage="Aktuálně" />
        }
        layout="list"
      >
        <Animation />
      </Group>

      <Group
        title={
          <FormattedMessage id="homepage.actual.title" defaultMessage=" " />
        }
        layout="grid"
      >
        <ValueBox
          visibility={{ variableId: 'PV_BATTERY' }}
          title={
            <FormattedMessage
              id="homepage.actual.battery"
              defaultMessage="Stav baterie"
            />
          }
          value={
            <FormattedValue
              variableId="PV_ACTUAL_SOC"
              opts={{
                style: 'percent',
                maximumFractionDigits: 0,
              }}
            />
          }
          icon={<Battery />}
          gridItem={{ size: 6 }}
        />

        <ValueBox
          visibility={{ variableId: 'THERMOSTAT_INSTALL' }}
          title={
            <FormattedMessage
              id="homepage.actual.thermostat"
              defaultMessage="Termostat"
            />
          }
          value={
            <FormattedValue
              variableId="THERMOSTAT_INFO[0].actualTemp"
              opts={{
                style: 'unit',
                unit: 'celsius',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          icon={<Thermometer />}
          gridItem={{ size: 6 }}
        />

        <ValueBox
          visibility={{
            variableIds: ['HW_TEMP_ENABLED', 'HW_INSTALL'],
          }}
          title={
            <FormattedMessage
              id="homepage.actual.boiler"
              defaultMessage="Bojler"
            />
          }
          value={
            <FormattedValue
              variableId="HW_TEMP"
              opts={{
                style: 'unit',
                unit: 'celsius',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          icon={<Boiler />}
          gridItem={{ size: 6 }}
        />

        {(ecooneHP || acondHP || nibeHP) && (
          <ValueBox
            visibility={{
              variableIds: ['HP_ENABLED'],
            }}
            title={
              <FormattedMessage
                id="homepage.actual.outdoor_temperature"
                defaultMessage="Venkovní teplota"
              />
            }
            value={
              <FormattedValue
                variableId="OUTDOOR_TEMP"
                opts={{
                  style: 'unit',
                  unit: 'celsius',
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                }}
              />
            }
            icon={<Thermometer />}
            gridItem={{ size: 6 }}
          />
        )}
      </Group>

      <Group
        title={
          <FormattedMessage
            id="homepage.today.title"
            defaultMessage="Denní statistika"
          />
        }
        layout="list"
      >
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="homepage.today.photovoltaics"
              defaultMessage="Výroba FVE"
            />
          }
          visibility={{ variableId: 'PV_INSTALL' }}
          value={
            <FormattedValue
              variableId={'STATS_PV_ENERGY_PRODUCED_TODAY'}
              opts={{
                style: 'unit',
                unit: 'kilowatt-hour',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          icon={<SolarPanel />}
        />

        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="homepage.today.consumed_from_grid"
              defaultMessage="Spotřeba ze sítě"
            />
          }
          value={
            <FormattedValue
              variableId={'STATS_EM_ENERGY_CONSUMED_TODAY'}
              opts={{
                style: 'unit',
                unit: 'kilowatt-hour',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          icon={<ElectricalGrid />}
          visibility={{ variableId: 'EM_INSTALL' }}
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="homepage.today.overflow"
              defaultMessage="Přetok do sítě"
            />
          }
          visibility={{ variableId: 'PV_INSTALL' }}
          value={
            <FormattedValue
              variableId={'STATS_EM_ENERGY_OVERFLOW_TODAY'}
              opts={{
                style: 'unit',
                unit: 'kilowatt-hour',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          icon={<ElectricalGrid />}
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="homepage.today.consumed"
              defaultMessage="Spotřeba domu"
            />
          }
          value={
            <FormattedValue
              variableId={'STATS_HOME_ENERGY_CONSUMED_TODAY'}
              opts={{
                style: 'unit',
                unit: 'kilowatt-hour',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          visibility={{ variableId: 'EM_INSTALL' }}
          icon={<House />}
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="homepage.today.consumed_hw"
              defaultMessage="Teplá voda"
            />
          }
          value={
            <FormattedValue
              variableId={'STATS_HW_ENERGY_PRODUCED_TODAY'}
              opts={{
                style: 'unit',
                unit: 'kilowatt-hour',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          icon={<Boiler />}
          visibility={{ variableId: 'HW_INSTALL' }}
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="homepage.today.consumed_hp"
              defaultMessage="Tepelné čerpadlo"
            />
          }
          value={
            <FormattedValue
              variableId={'STATS_HP_ENERGY_CONSUMED_TODAY'}
              opts={{
                style: 'unit',
                unit: 'kilowatt-hour',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          icon={<HeatPump />}
          visibility={{ variableId: 'HP_ENABLED' }}
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="homepage.today.consumed_WB"
              defaultMessage="Wallbox"
            />
          }
          value={
            <FormattedValue
              variableId={'STATS_WB_ENERGY_CONSUMPTION_TODAY'}
              opts={{
                style: 'unit',
                unit: 'kilowatt-hour',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          icon={<Wallbox sx={{ width: 20 }} />}
          visibility={{ variableId: 'WB_INSTALL' }}
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="homepage.today.wind"
              defaultMessage="Výroba větrné elektrárny"
            />
          }
          visibility={{ variableId: 'WIND_INSTALL' }}
          value={
            <FormattedValue
              variableId={'WIND_PRODUCED_TODAY'}
              opts={{
                style: 'unit',
                unit: 'kilowatt-hour',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          icon={<Wind />}
        />
      </Group>
    </>
  );
};

export default Home;
